'use client';

import { useState } from 'react';
import Link from 'next/link';

import { Box, Paper, Popover, Stack, Tooltip, Typography } from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { toast } from 'sonner';

import { EmailToggle } from '@/components/ui/EmailToggle';
import { getDefaultFrequencyDays } from '@/libs/email/email-preference-defaults';
import { REPORT_TYPES, SCOPE_TYPES } from '@/libs/email/report-types';

import { subscribeToFundReportAction } from '../actions';

const AVAILABLE_FREQUENCIES = [7, 15, 30];
const TOGGLE_LABEL = 'Resumos deste fundo';
const TOOLTIP_LOGGED_OUT = 'Inscreva-se nos alertas para esse fundo';

interface FundReportPreference {
  id: number;
  enabled: boolean;
  frequencyDays: number | null;
}

interface FundSubscribeBellProps {
  ticker: string;
  isLoggedIn: boolean;
  existingPreference: FundReportPreference | null;
  g2rsMode?: boolean;
}

export const FundSubscribeBell = ({
  ticker,
  isLoggedIn,
  existingPreference,
  g2rsMode = false,
}: FundSubscribeBellProps) => {
  const [preference, setPreference] = useState<FundReportPreference | null>(existingPreference);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isSubscribing, setIsSubscribing] = useState(false);

  if (g2rsMode) return null;

  const open = Boolean(anchorEl);
  const popoverId = open ? `fund-subscribe-popover-${ticker}` : undefined;
  const subscribed = preference?.enabled ?? false;
  const frequencyHint = preference?.frequencyDays
    ? preference.frequencyDays === 7
      ? 'semanal'
      : preference.frequencyDays === 15
        ? 'quinzenal'
        : preference.frequencyDays === 30
          ? 'mensal'
          : `a cada ${preference.frequencyDays} dias`
    : null;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSubscribe = async () => {
    setIsSubscribing(true);
    try {
      const formData = new FormData();
      formData.set('ticker', ticker);
      const result = await subscribeToFundReportAction(formData);
      if (result.success && result.preference) {
        setPreference(result.preference);
        toast.success('Inscrição realizada com sucesso');
      } else {
        toast.error('Não foi possível realizar a inscrição');
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  const bellButton = (
    <Box
      component={isLoggedIn ? 'button' : Link}
      type={isLoggedIn ? 'button' : undefined}
      href={!isLoggedIn ? `/login?redirect=/ativo/${ticker}` : undefined}
      onClick={isLoggedIn ? handleOpen : undefined}
      aria-label={
        isLoggedIn
          ? subscribed
            ? 'Configurar alertas deste fundo'
            : 'Inscrever-se nos alertas deste fundo'
          : TOOLTIP_LOGGED_OUT
      }
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        borderRadius: '50%',
        cursor: 'pointer',
        border: '1px solid',
        flexShrink: 0,
        fontSize: 18,
        lineHeight: 1,
        transition: 'all 0.2s ease',
        ...(subscribed
          ? {
              background: 'hsl(var(--secondary))',
              borderColor: 'hsl(var(--secondary))',
              color: 'hsl(var(--secondary-foreground))',
              boxShadow: '0 4px 12px hsl(var(--secondary) / 0.32)',
              '&:hover': {
                background: 'hsl(var(--secondary) / 0.92)',
                borderColor: 'hsl(var(--secondary) / 0.92)',
                boxShadow: '0 6px 16px hsl(var(--secondary) / 0.45)',
              },
            }
          : {
              background: 'transparent',
              borderColor: 'hsl(var(--secondary) / 0.6)',
              borderWidth: '1.5px',
              color: 'hsl(var(--secondary))',
              '&:hover': {
                background: 'hsl(var(--secondary) / 0.08)',
                borderColor: 'hsl(var(--secondary))',
              },
            }),
      }}
    >
      <Box component='span' aria-hidden='true' sx={{ display: 'inline-flex', alignItems: 'center' }}>
        {subscribed ? (
          <NotificationsActiveOutlinedIcon fontSize='small' />
        ) : (
          <NotificationsNoneOutlinedIcon fontSize='small' />
        )}
      </Box>
    </Box>
  );

  const statusPill = (
    <Box
      component={isLoggedIn ? 'button' : Link}
      type={isLoggedIn ? 'button' : undefined}
      href={!isLoggedIn ? `/login?redirect=/ativo/${ticker}` : undefined}
      onClick={isLoggedIn ? handleOpen : undefined}
      sx={() => {
        return {
          px: 0.25,
          py: 0.25,
          background: 'transparent',
          border: 'none',
          fontSize: { xs: '0.92rem', md: '1rem' },
          fontWeight: 700,
          letterSpacing: 0.1,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
          textAlign: 'left',
          whiteSpace: { xs: 'normal', md: 'nowrap' },
          cursor: 'pointer',
          fontFamily: 'inherit',
          lineHeight: 1.2,
          color: 'hsl(var(--muted-foreground))',
          transition: 'all 0.2s ease',
          '&:hover': {
            color: 'hsl(var(--foreground))',
          },
        };
      }}
    >
      {!isLoggedIn
        ? 'Faça login para receber alertas deste fundo por email'
        : subscribed
          ? `Recebendo alertas${frequencyHint ? ` (${frequencyHint})` : ''}`
          : 'Receba alertas deste fundo'}
    </Box>
  );

  return (
    <>
      <Stack direction='row' spacing={1.25} alignItems='center' sx={{ minWidth: 0 }}>
        {isLoggedIn ? (
          bellButton
        ) : (
          <Tooltip title={TOOLTIP_LOGGED_OUT} arrow>
            {bellButton}
          </Tooltip>
        )}
        {statusPill}
      </Stack>

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { mt: 1, p: 2, width: 320, borderRadius: 2 } },
        }}
      >
        <Stack spacing={1.25}>
          <Typography variant='subtitle2' fontWeight={700}>
            Alertas por email — {ticker}
          </Typography>
          {preference ? (
            <Box>
              <EmailToggle
                preferenceId={preference.id}
                enabled={preference.enabled}
                frequencyDays={preference.frequencyDays ?? getDefaultFrequencyDays(REPORT_TYPES.FUND_REPORT)}
                availableFrequencies={AVAILABLE_FREQUENCIES}
                reportType={REPORT_TYPES.FUND_REPORT}
                scopeType={SCOPE_TYPES.FUND}
                scopeId={ticker}
                label={TOGGLE_LABEL}
                onPreferenceChange={(next) => {
                  setPreference({
                    id: next.id ?? preference.id,
                    enabled: next.enabled,
                    frequencyDays: next.frequencyDays,
                  });
                }}
              />
            </Box>
          ) : (
            <Paper variant='outlined' sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'transparent' }}>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                Receba um resumo deste fundo por email na frequência que preferir.
              </Typography>
              <Box>
                <button
                  type='button'
                  onClick={handleSubscribe}
                  disabled={isSubscribing}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: '1px solid hsl(var(--primary))',
                    background: 'hsl(var(--primary))',
                    color: 'hsl(var(--primary-foreground))',
                    cursor: isSubscribing ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                  }}
                >
                  {isSubscribing ? 'Inscrevendo...' : 'Quero receber resumos deste fundo'}
                </button>
              </Box>
            </Paper>
          )}
        </Stack>
      </Popover>
    </>
  );
};
